<template>
  <div class="data-stats-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <div class="title-section">
          <h2 class="page-title">数据统计</h2>
          <p class="page-subtitle">查看钻石收益趋势</p>
        </div>
      </div>
      <div class="header-actions">
        <div class="rate-setting-header">
          <input 
            type="number" 
            v-model.number="rmbRate" 
            @change="saveRmbRate"
            class="rate-input-header"
            min="0.01"
            step="0.01"
          >
          <span class="rate-label-header">元/1000钻</span>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-content">
          <div class="stat-value">{{ formatNumber(totalDiamonds) }}</div>
          <div class="stat-label">总钻石数</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-content">
          <div class="stat-value">{{ diamondToRmb(totalDiamonds) }}</div>
          <div class="stat-label">总收益（人民币）</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-content">
          <div class="stat-value">{{ trendData.length }}</div>
          <div class="stat-label">统计天数</div>
        </div>
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="chart-section">
      <div class="chart-header">
        <h3>钻石收益趋势</h3>
        <div class="chart-controls">
          <el-select v-model="selectedDays" @change="loadDiamondTrends" size="small" style="width: 120px;">
            <el-option label="最近7天" value="7" />
            <el-option label="最近15天" value="15" />
            <el-option label="最近30天" value="30" />
            <el-option label="最近60天" value="60" />
          </el-select>
        </div>
      </div>
      
      <!-- 无数据提示 -->
      <div v-if="!loading && trendData.length < 2" class="no-data">
        <div class="no-data-text">
          <h4>暂无足够数据</h4>
          <p>需要至少2天的数据才能显示趋势图表</p>
        </div>
      </div>

      <!-- ECharts图表 -->
      <div v-else>
        <!-- 桌面端图表容器 -->
        <div class="charts-container desktop-charts">
          <!-- 总钻石数趋势图 -->
          <div class="chart-wrapper">
            <h4>总钻石数趋势</h4>
            <div ref="totalChartRef" class="chart" style="width: 100%; height: 220px;"></div>
          </div>
          
          <!-- 总钻石增量趋势图 -->
          <div class="chart-wrapper">
            <h4>总钻石增量趋势</h4>
            <div ref="totalEarningChartRef" class="chart" style="width: 100%; height: 220px;"></div>
          </div>
        </div>

        <!-- 移动端图表（直接显示，无容器包装） -->
        <div class="mobile-charts">
          <!-- 总钻石数趋势图 -->
          <div class="mobile-chart">
            <h4>总钻石数趋势</h4>
            <div ref="totalChartRefMobile" class="chart" style="width: 100%; height: 260px;"></div>
          </div>
          
          <!-- 总钻石增量趋势图 -->
          <div class="mobile-chart">
            <h4>总钻石增量趋势</h4>
            <div ref="totalEarningChartRefMobile" class="chart" style="width: 100%; height: 260px;"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 大区钻石统计 -->
    <div class="region-stats-section">
      <div class="section-header">
        <h3>大区钻石统计</h3>
        <el-select v-model="selectedGame" @change="loadRegionStats" size="small" style="width: 180px;">
          <el-option label="尤弥尔传奇国际服" value="尤弥尔传奇国际服" />
          <el-option label="尤弥尔传奇韩服" value="尤弥尔传奇韩服" />
        </el-select>
      </div>
      
      <!-- 无数据提示 -->
      <div v-if="!regionLoading && regionStats.length === 0" class="no-data">
        <div class="no-data-text">
          <h4>暂无数据</h4>
          <p>该游戏暂无大区钻石统计数据</p>
        </div>
      </div>

      <!-- 大区统计表格 -->
      <div v-else class="region-stats-table">
        <table>
          <thead>
            <tr>
              <th>大区</th>
              <th>窗口数</th>
              <th>钻石总数</th>
              <th>收益（人民币）</th>
              <th>占比</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(stat, index) in sortedRegionStats" :key="index">
              <td class="region-name">{{ stat.regionPart || stat.region_part || '未知' }}</td>
              <td class="window-count">{{ stat.windowCount || stat.window_count || 0 }}</td>
              <td class="diamond-value">{{ formatNumber(stat.totalDiamonds || stat.total_diamonds || 0) }}</td>
              <td class="rmb-value">¥{{ diamondToRmb(stat.totalDiamonds || stat.total_diamonds || 0) }}</td>
              <td class="percentage">
                <div class="percentage-bar">
                  <div 
                    class="percentage-fill" 
                    :style="{ width: getPercentage(stat.totalDiamonds || stat.total_diamonds || 0) + '%' }"
                  ></div>
                  <span class="percentage-text">{{ getPercentage(stat.totalDiamonds || stat.total_diamonds || 0) }}%</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import * as echarts from 'echarts'
import { getDiamondTrends, getRegionDiamondStats } from '../api/roles'

export default {
  name: 'DataStats',
  setup() {
    const loading = ref(false)
    const totalChartRef = ref(null)
    const totalEarningChartRef = ref(null)
    const totalChartRefMobile = ref(null)
    const totalEarningChartRefMobile = ref(null)
    const totalChartInstance = ref(null)
    const totalEarningChartInstance = ref(null)
    const totalChartInstanceMobile = ref(null)
    const totalEarningChartInstanceMobile = ref(null)
    const selectedDays = ref('15')
    const trendData = ref([])
    const rmbRate = ref(37) // 默认1000钻=37元
    const selectedGame = ref('尤弥尔传奇国际服')
    const regionStats = ref([])
    const regionLoading = ref(false)

    // 计算统计数据
    const totalDiamonds = computed(() => {
      if (trendData.value.length === 0) return 0
      return trendData.value[trendData.value.length - 1]?.totalDiamonds || 0
    })


    // 钻石转人民币
    const diamondToRmb = (diamonds) => {
      return ((diamonds / 1000) * rmbRate.value).toFixed(2)
    }

    // 保存汇率到cookie
    const saveRmbRate = () => {
      document.cookie = `rmbRate=${rmbRate.value}; path=/; max-age=${365 * 24 * 60 * 60}` // 保存1年
    }

    // 从cookie加载汇率
    const loadRmbRate = () => {
      const cookies = document.cookie.split(';')
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=')
        if (name === 'rmbRate') {
          const rate = parseFloat(value)
          if (!isNaN(rate) && rate > 0) {
            rmbRate.value = rate
          }
          break
        }
      }
    }

    // 格式化数字
    const formatNumber = (num) => {
      if (num == null) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    // 格式化日期（增加周几标识）
    const formatDate = (dateStr) => {
      const date = new Date(dateStr)
      const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      const weekday = weekdays[date.getDay()]
      return `${date.getMonth() + 1}/${date.getDate()}(${weekday})`
    }

    // 获取移动端优化的grid配置
    const getMobileGridConfig = () => ({
      left: '20px',
      right: '10px', 
      top: '10px',
      bottom: '30px',
      containLabel: true
    })

    // 获取桌面端grid配置
    const getDesktopGridConfig = () => ({
      left: '35px',
      right: '10px',
      top: '10px', 
      bottom: '25px',
      containLabel: true
    })

    // 加载钻石趋势数据
    const loadDiamondTrends = async () => {
      loading.value = true
      try {
        const response = await getDiamondTrends(selectedDays.value)
        
        if (response.code === 200) {
          trendData.value = response.data || []
          await nextTick()
          if (trendData.value.length >= 2) {
            updateTotalChart()
            updateTotalEarningChart()
            updateMobileCharts()
          }
        } else {
          throw new Error(response.message || '获取数据失败')
        }
      } catch (error) {
        console.error('加载钻石趋势数据失败:', error)
        ElMessage.error('加载数据失败: ' + (error.message || '网络错误'))
      } finally {
        loading.value = false
      }
    }

    // 更新总钻石数图表
    const updateTotalChart = () => {
      if (!totalChartRef.value || trendData.value.length < 2) return

      if (!totalChartInstance.value) {
        totalChartInstance.value = echarts.init(totalChartRef.value)
      }

      const dates = trendData.value.map(item => formatDate(item.date))
      const totalDiamondsData = trendData.value.map(item => item.totalDiamonds)

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        grid: getDesktopGridConfig(),
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: {
            fontSize: 11,
            rotate: 44
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: function(value) {
              if (value >= 10000) {
                return (value / 10000).toFixed(1) + 'W'
              }
              return value
            }
          }
        },
        series: [{
          name: '总钻石数',
          type: 'line',
          data: totalDiamondsData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: {
            color: '#409EFF',
            width: 2.5
          },
          itemStyle: {
            color: '#409EFF'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.05)' }
              ]
            }
          }
        }]
      }

      totalChartInstance.value.setOption(option)
    }

    // 更新总钻石增量图表
    const updateTotalEarningChart = () => {
      if (!totalEarningChartRef.value || trendData.value.length < 2) return

      if (!totalEarningChartInstance.value) {
        totalEarningChartInstance.value = echarts.init(totalEarningChartRef.value)
      }

      const dates = trendData.value.map(item => formatDate(item.date))
      const dailyEarningData = trendData.value.map(item => item.dailyEarning || 0)

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        grid: getDesktopGridConfig(),
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: {
            fontSize: 11,
            rotate: 44
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: function(value) {
              if (value >= 10000) {
                return (value / 10000).toFixed(1) + 'W'
              }
              return value
            }
          }
        },
        series: [{
          name: '总钻石增量',
          type: 'bar',
          data: dailyEarningData,
          itemStyle: {
            color: '#E6A23C',
            borderRadius: [2, 2, 0, 0]
          }
        }]
      }

      totalEarningChartInstance.value.setOption(option)
    }


    // 更新移动端图表
    const updateMobileCharts = () => {
      if (trendData.value.length < 2) return

      const dates = trendData.value.map(item => formatDate(item.date))
      const totalDiamondsData = trendData.value.map(item => item.totalDiamonds)
      const dailyEarningData = trendData.value.map(item => item.dailyEarning || 0)

      // 总钻石数趋势图（移动端）
      if (totalChartRefMobile.value) {
        if (!totalChartInstanceMobile.value) {
          totalChartInstanceMobile.value = echarts.init(totalChartRefMobile.value)
        }
        totalChartInstanceMobile.value.setOption({
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' }
          },
          grid: getMobileGridConfig(),
          xAxis: {
            type: 'category',
            data: dates,
            axisLabel: { fontSize: 11, rotate: 44 }
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              fontSize: 10,
              formatter: function(value) {
                if (value >= 10000) {
                  return (value / 10000).toFixed(1) + 'W'
                }
                return value
              }
            }
          },
          series: [{
            name: '总钻石数',
            type: 'line',
            data: totalDiamondsData,
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            lineStyle: { color: '#409EFF', width: 2 },
            itemStyle: { color: '#409EFF' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
                  { offset: 1, color: 'rgba(64, 158, 255, 0.05)' }
                ]
              }
            }
          }]
        })
      }

      // 总钻石增量趋势图（移动端）
      if (totalEarningChartRefMobile.value) {
        if (!totalEarningChartInstanceMobile.value) {
          totalEarningChartInstanceMobile.value = echarts.init(totalEarningChartRefMobile.value)
        }
        totalEarningChartInstanceMobile.value.setOption({
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' }
          },
          grid: getMobileGridConfig(),
          xAxis: {
            type: 'category',
            data: dates,
            axisLabel: { fontSize: 11, rotate: 44 }
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              fontSize: 10,
              formatter: function(value) {
                if (value >= 10000) {
                  return (value / 10000).toFixed(1) + 'W'
                }
                return value
              }
            }
          },
          series: [{
            name: '总钻石增量',
            type: 'bar',
            data: dailyEarningData,
            itemStyle: {
              color: '#E6A23C',
              borderRadius: [2, 2, 0, 0]
            }
          }]
        })
      }
    }

    // 窗口大小变化时重新调整图表
    const handleResize = () => {
      if (totalChartInstance.value) {
        totalChartInstance.value.resize()
      }
      if (totalEarningChartInstance.value) {
        totalEarningChartInstance.value.resize()
      }
      if (totalChartInstanceMobile.value) {
        totalChartInstanceMobile.value.resize()
      }
      if (totalEarningChartInstanceMobile.value) {
        totalEarningChartInstanceMobile.value.resize()
      }
    }

    // 加载大区钻石统计
    const loadRegionStats = async () => {
      if (!selectedGame.value) return
      regionLoading.value = true
      try {
        const response = await getRegionDiamondStats(selectedGame.value)
        if (response.code === 200) {
          regionStats.value = response.data || []
        } else {
          throw new Error(response.message || '获取数据失败')
        }
      } catch (error) {
        console.error('加载大区钻石统计失败:', error)
        ElMessage.error('加载大区钻石统计失败: ' + (error.message || '网络错误'))
        regionStats.value = []
      } finally {
        regionLoading.value = false
      }
    }

    // 计算总钻石数（用于计算占比）
    const totalRegionDiamonds = computed(() => {
      return regionStats.value.reduce((sum, stat) => {
        const diamonds = stat.totalDiamonds || stat.total_diamonds || 0
        return sum + diamonds
      }, 0)
    })

    // 计算占比
    const getPercentage = (diamonds) => {
      if (totalRegionDiamonds.value === 0) return 0
      return ((diamonds / totalRegionDiamonds.value) * 100).toFixed(1)
    }

    // 按钻石数排序
    const sortedRegionStats = computed(() => {
      return [...regionStats.value].sort((a, b) => {
        const aDiamonds = a.totalDiamonds || a.total_diamonds || 0
        const bDiamonds = b.totalDiamonds || b.total_diamonds || 0
        return bDiamonds - aDiamonds
      })
    })

    onMounted(() => {
      loadRmbRate() // 加载汇率设置
      loadDiamondTrends()
      loadRegionStats() // 加载大区钻石统计
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      if (totalChartInstance.value) {
        totalChartInstance.value.dispose()
      }
      if (totalEarningChartInstance.value) {
        totalEarningChartInstance.value.dispose()
      }
      if (totalChartInstanceMobile.value) {
        totalChartInstanceMobile.value.dispose()
      }
      if (totalEarningChartInstanceMobile.value) {
        totalEarningChartInstanceMobile.value.dispose()
      }
      window.removeEventListener('resize', handleResize)
    })

    return {
      loading,
      totalChartRef,
      totalEarningChartRef,
      totalChartRefMobile,
      totalEarningChartRefMobile,
      selectedDays,
      trendData,
      totalDiamonds,
      rmbRate,
      diamondToRmb,
      saveRmbRate,
      formatNumber,
      loadDiamondTrends,
      selectedGame,
      regionStats,
      regionLoading,
      loadRegionStats,
      sortedRegionStats,
      getPercentage
    }
  }
}
</script>

<style scoped>
.data-stats-container {
  padding: 0;
  max-width: none;
  margin: 0;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8e9ea;
}

.title-section h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
}

.page-subtitle {
  margin: 4px 0 0 0;
  color: #666;
  font-size: 14px;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #409EFF;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.refresh-btn:hover {
  background: #337ECC;
}

.rate-setting-header {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.rate-input-header {
  width: 60px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  background: transparent;
  color: #333;
}

.rate-input-header:focus {
  outline: none;
}

.rate-label-header {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}


/* 统计卡片 */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.rate-setting {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.rate-input {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  background: #f8f9fa;
}

.rate-input:focus {
  outline: none;
  border-color: #409EFF;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.rate-label {
  font-size: 12px;
  color: #666;
  font-weight: normal;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-content {
  text-align: center;
  width: 100%;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #1d1d1f;
  margin-bottom: 4px;
}

.stat-label {
  color: #666;
  font-size: 12px;
}

/* 图表区域 */
.chart-section {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.chart-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
}

.no-data {
  text-align: center;
  padding: 80px 20px;
  color: #999;
}

.no-data-text h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
  color: #666;
}

.no-data-text p {
  margin: 0;
  font-size: 14px;
}

/* 桌面端图表容器 */
.desktop-charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart-wrapper {
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* 移动端图表容器 */
.mobile-charts {
  display: none;
}

.mobile-chart {
  background: white;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.chart-wrapper h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
  text-align: center;
}

.chart {
  border-radius: 8px;
  overflow: hidden;
}


/* 响应式设计 */

@media (max-width: 768px) {
  .data-stats-container {
    padding: 8px;
  }

  .page-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    padding: 16px 8px;
    margin-bottom: 16px;
  }

  .stats-cards {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 0 4px;
    margin-bottom: 16px;
  }

  .stat-card {
    padding: 12px;
  }
  
  .stat-value {
    font-size: 20px;
  }
  
  .stat-label {
    font-size: 11px;
  }

  .chart-section {
    padding: 8px;
    margin-bottom: 0;
  }

  .chart-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    margin-bottom: 12px;
  }

  .chart-header h3 {
    font-size: 16px;
  }

  /* 隐藏桌面端图表 */
  .desktop-charts {
    display: none;
  }

  /* 显示移动端图表 */
  .mobile-charts {
    display: block;
    padding: 0;
  }

  .mobile-chart {
    margin-bottom: 8px;
    padding: 8px;
  }

  .mobile-chart h4 {
    margin: 0 0 6px 0;
    font-size: 13px;
    font-weight: 600;
    color: #1d1d1f;
  }
}

/* 大区钻石统计区域 */
.region-stats-section {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
}

.region-stats-table {
  overflow-x: auto;
}

.region-stats-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.region-stats-table thead {
  background: #f5f7fa;
}

.region-stats-table th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #606266;
  font-size: 12px;
  border-bottom: 2px solid #e4e7ed;
}

.region-stats-table th:first-child {
  border-top-left-radius: 6px;
}

.region-stats-table th:last-child {
  border-top-right-radius: 6px;
}

.region-stats-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.region-stats-table tbody tr:hover {
  background-color: #fafafa;
}

.region-stats-table tbody tr:last-child {
  border-bottom: none;
}

.region-stats-table td {
  padding: 10px 12px;
  color: #303133;
}

.region-name {
  font-weight: 500;
  color: #409EFF;
}

.window-count {
  font-weight: 500;
  color: #606266;
  font-size: 14px;
  text-align: center;
}

.diamond-value {
  font-weight: 600;
  color: #1d1d1f;
  font-size: 14px;
}

.rmb-value {
  font-weight: 600;
  color: #67C23A;
  font-size: 14px;
}

.percentage {
  min-width: 150px;
}

.percentage-bar {
  position: relative;
  width: 100%;
  height: 24px;
  background: #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
}

.percentage-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #409EFF 0%, #66b1ff 100%);
  border-radius: 12px;
  transition: width 0.3s ease;
}

.percentage-text {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: 600;
  color: #303133;
  z-index: 1;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .region-stats-section {
    padding: 12px;
    margin-bottom: 16px;
  }

  .section-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    margin-bottom: 12px;
  }

  .section-header h3 {
    font-size: 16px;
  }

  .region-stats-table {
    font-size: 12px;
  }

  .region-stats-table th,
  .region-stats-table td {
    padding: 10px 8px;
    font-size: 12px;
  }

  .diamond-value,
  .rmb-value {
    font-size: 13px;
  }

  .percentage {
    min-width: 120px;
  }

  .percentage-bar {
    height: 20px;
  }

  .percentage-text {
    font-size: 11px;
  }
}
</style>
